import React from 'react';
import PropTypes from 'prop-types';

import { PowerFunnelSurfaces } from '../../Analytics/constants';
import useFableticsCta from '../../hooks/useFableticsCta';
import { IMAGES, COPY } from '../../Fabletics/constants';
import { userCanAccessGlobalPrivileges } from '../../../utils/user';
import FableticsPastDueMessage from './FableticsPastDueMessage';
import useAppleSubscriptionPopup from './useAppleSubscriptionPopup';
import styles from './fableticsControlBanner.scss';

const SUBHEAD_BY_TIER = {
    monthly: (
        <>
            {'Upgrade to annual Power and get '}
            <span className={styles.bodyEmphasis}>$250 in travel value</span>
            {' instantly.'}
        </>
    ),
    free: (
        <>
            {'Sign up for Power and get '}
            <span className={styles.bodyEmphasis}>$250 in travel value</span>
            {' instantly.'}
        </>
    ),
};

const FableticsControlBanner = ({ surface, variant }) => {
    const { open: openAppleSubscriptionPopup, popup: appleSubscriptionPopup } = useAppleSubscriptionPopup();
    const cta = useFableticsCta({
        surface,
        variant,
        trackImpressions: true,
        onAppleManageClick: openAppleSubscriptionPopup,
    });
    const {
        buttonText,
        fableticsCtaUrl,
        handleCtaClick,
        handleLearnMoreClick,
        handleVisitGlobalPrivilegesClick,
        hasPastDueSubscription,
        user,
        userStatus,
    } = cta;

    // Entitlement is plan-driven: presence of `ctaUrl` in `memberThirdPartyPlans`
    // is the signal — backend only emits a Fabletics row for users it has
    // actually provisioned, so we don't gate further on subscription tier or
    // country. Past-due users bypass the entitled layouts so they see the
    // single "Resume Power" CTA per the past-due designs.
    const isEntitled = !!fableticsCtaUrl && !hasPastDueSubscription;
    // Annual subscribers always get GP entitlement (per the matrix), even
    // when their `memberThirdPartyPlans` doesn't carry a GP row. Use the
    // broader access check so annual + Fabletics-only users still see the
    // dual-card layout.
    const showGpCard = userCanAccessGlobalPrivileges(user);

    // GP-only audience: user can access GP but isn't Fabletics-entitled and
    // isn't past-due. Past-due users skip this branch so they land on the
    // non-entitled "Tennis Passport" JSX with the Resume Power CTA.
    const showGpOnlyCard = !isEntitled && !hasPastDueSubscription && showGpCard;

    // Control banner uses "Sign Up" for the free-user CTA; the other variants
    // keep the shared "Get Power" copy from useFableticsCta.
    const ctaText = buttonText === 'Get Power' ? 'Sign Up' : buttonText;

    if (isEntitled && showGpCard) {
        return (
            <div
                className={styles.fableticsControlBanner}
                data-testid="fabletics.banner.control.container"
            >
                <div className={styles.dualBannerInner}>
                    <div className={styles.dualImageSection}>
                        <div
                            className={`${styles.imagePanel} ${styles.imagePanelTennis}`}
                            data-testid="fabletics.banner.control.image-tennis"
                        >
                            <img
                                src={IMAGES.tennisWomen}
                                alt="Two tennis players in Fabletics apparel"
                                className={styles.imagePanelImg}
                            />
                        </div>
                        <div
                            className={`${styles.imagePanel} ${styles.imagePanelAirport}`}
                            data-testid="fabletics.banner.control.image-airport"
                        >
                            <img
                                src={IMAGES.airport}
                                alt="Tennis player at airport with UTR Sports bag"
                                className={styles.imagePanelImg}
                            />
                        </div>
                    </div>
                    <div className={styles.dualTextColumn}>
                        <div
                            className={styles.dualHeadline}
                            data-testid="fabletics.banner.control.dual-headline"
                        >
                            <p className={styles.dualHeadlineTitle}>
                                {COPY.dualHeadlineTitle}
                            </p>
                            <p className={styles.dualHeadlineSubhead}>
                                {COPY.dualHeadlineValue}
                            </p>
                        </div>
                        <div className={styles.dualCardRow}>
                            <div className={`${styles.perkCard} ${styles.perkCardFabletics}`}>
                                <p
                                    className={styles.perkCardTitle}
                                    data-testid="fabletics.banner.control.fabletics-card.title"
                                >
                                    Fabletics UTR VIP
                                </p>
                                <p className={styles.perkCardBody}>
                                    {COPY.fableticsCard}
                                </p>
                                <button
                                    type="button"
                                    className={`${styles.ctaButton} ${styles.ctaButtonFixed}`}
                                    data-testid="fabletics.banner.control.fabletics-card.cta"
                                    onClick={handleCtaClick}
                                >
                                    Visit Fabletics
                                </button>
                            </div>
                            <div className={`${styles.perkCard} ${styles.perkCardGp}`}>
                                <p
                                    className={styles.perkCardTitle}
                                    data-testid="fabletics.banner.control.gp-card.title"
                                >
                                    Global Privileges
                                </p>
                                <p className={styles.perkCardBody}>
                                    $250+ value in travel benefits, incl. $100 voucher
                                    and up to 60% off on 1.4M+ hotels
                                </p>
                                <button
                                    type="button"
                                    className={`${styles.ctaButton} ${styles.ctaButtonFixed}`}
                                    data-testid="fabletics.banner.control.gp-card.cta"
                                    onClick={handleVisitGlobalPrivilegesClick}
                                >
                                    Visit Global Privileges
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {appleSubscriptionPopup}
            </div>
        );
    }

    if (isEntitled && !showGpCard) {
        return (
            <div
                className={styles.fableticsControlBanner}
                data-testid="fabletics.banner.control.container"
            >
                <div className={styles.singleBannerInner}>
                    <div className={styles.singleImageSection}>
                        <img
                            src={IMAGES.tennisWomen}
                            alt="Two tennis players in Fabletics apparel"
                            data-testid="fabletics.banner.control.image-tennis"
                            className={styles.singleImage}
                        />
                    </div>
                    <div className={styles.singleCardRow}>
                        <div className={styles.singleCard}>
                            <div className={styles.singleCardCopy}>
                                <p
                                    className={styles.singleCardTitle}
                                    data-testid="fabletics.banner.control.fabletics-card.title"
                                >
                                    Fabletics UTR VIP
                                </p>
                                <p className={styles.singleCardBody}>
                                    {COPY.fableticsCard}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={`${styles.ctaButton} ${styles.ctaButtonFixed}`}
                                data-testid="fabletics.banner.control.fabletics-card.cta"
                                onClick={handleCtaClick}
                            >
                                Visit Fabletics
                            </button>
                        </div>
                    </div>
                </div>
                {appleSubscriptionPopup}
            </div>
        );
    }

    // GP-only audience (annual + US without a Fabletics row, or any non-annual
    // user with a GP plan, per Figma 4345:11851).
    if (showGpOnlyCard) {
        return (
            <div
                className={styles.fableticsControlBanner}
                data-testid="fabletics.banner.control.container"
            >
                <div className={styles.singleBannerInner}>
                    <div className={`${styles.singleImageSection} ${styles.singleImageSectionAirport}`}>
                        <img
                            src={IMAGES.airport}
                            alt="Tennis player at airport with UTR Sports bag"
                            data-testid="fabletics.banner.control.image-airport"
                            className={`${styles.singleImage} ${styles.singleImageAirport}`}
                        />
                    </div>
                    <div className={styles.singleCardRow}>
                        <div className={styles.singleCard}>
                            <div className={styles.singleCardCopy}>
                                <p
                                    className={styles.singleCardTitle}
                                    data-testid="fabletics.banner.control.gp-card.title"
                                >
                                    Global Privileges
                                </p>
                                <p className={styles.singleCardBody}>
                                    $250+ value in travel benefits, incl. $100 voucher
                                    and up to 60% off on 1.4M+ hotels
                                </p>
                            </div>
                            <button
                                type="button"
                                className={`${styles.ctaButton} ${styles.ctaButtonFixed}`}
                                data-testid="fabletics.banner.control.gp-card.cta"
                                onClick={handleVisitGlobalPrivilegesClick}
                            >
                                Visit Global Privileges
                            </button>
                        </div>
                    </div>
                </div>
                {appleSubscriptionPopup}
            </div>
        );
    }

    return (
        <div
            className={styles.fableticsControlBanner}
            data-testid="fabletics.banner.control.container"
        >
            <div className={styles.bannerInner}>
                <div className={styles.textSection}>
                    <p
                        className={styles.headline}
                        data-testid="fabletics.banner.control.headline"
                    >
                        Your Tennis Passport Awaits.
                    </p>
                    <p
                        className={styles.subhead}
                        data-testid="fabletics.banner.control.subhead"
                    >
                        {SUBHEAD_BY_TIER[userStatus] || SUBHEAD_BY_TIER.free}
                    </p>
                    <p className={styles.body}>
                        Plus,
                        {' '}
                        <span className={styles.bodyEmphasis}>
                            $1000+ in free membership, VIP prices (up to 50% off),
                            quarterly rewards
                        </span>
                        , and more from
                    </p>
                    <img
                        src={IMAGES.fableticsLogo}
                        alt="Fabletics"
                        className={styles.fableticsLogo}
                    />
                </div>
                <div className={styles.imageSection}>
                    <div
                        className={`${styles.imagePanel} ${styles.imagePanelAirport}`}
                        data-testid="fabletics.banner.control.image-airport"
                    >
                        <img
                            src={IMAGES.airport}
                            alt="Tennis player at airport with UTR Sports bag"
                            className={styles.imagePanelImg}
                        />
                    </div>
                    <div
                        className={`${styles.imagePanel} ${styles.imagePanelTennis}`}
                        data-testid="fabletics.banner.control.image-tennis"
                    >
                        <img
                            src={IMAGES.tennisWomen}
                            alt="Two tennis players in Fabletics apparel"
                            className={styles.imagePanelImg}
                        />
                    </div>
                </div>
                <div className={styles.ctaSection}>
                    <FableticsPastDueMessage
                        hasPastDueSubscription={hasPastDueSubscription}
                        className={styles.pastDueMessage}
                        testId="fabletics.banner.control.past-due-message"
                    />
                    <button
                        type="button"
                        className={styles.ctaButton}
                        data-testid="fabletics.banner.control.primary-cta"
                        onClick={handleCtaClick}
                    >
                        {ctaText}
                    </button>
                    <button
                        type="button"
                        className={styles.learnMore}
                        data-testid="fabletics.banner.control.learn-more"
                        onClick={handleLearnMoreClick}
                    >
                        Learn more
                    </button>
                </div>
            </div>
            {appleSubscriptionPopup}
        </div>
    );
};

FableticsControlBanner.propTypes = {
    surface: PropTypes.oneOf([PowerFunnelSurfaces.HOME, PowerFunnelSurfaces.POWER_PERKS_PAGE]),
    variant: PropTypes.string,
};

FableticsControlBanner.defaultProps = {
    surface: PowerFunnelSurfaces.HOME,
    variant: 'control',
};

export default FableticsControlBanner;
